<template>
  <div class="home-page">
    <section class="stats-section">
      <div class="section-head">
        <div>
          <h2>实时统计概览</h2>
        </div>
      </div>
      <div class="stats-cards">
        <div class="stat-card" v-for="(card, index) in statCards" :key="index">
          <div class="card-icon" :style="{ background: card.gradient }">
            <i :class="card.icon"></i>
          </div>
          <div class="card-content">
            <div class="card-label">{{ card.label }}</div>
            <div class="card-value">{{ card.value }}{{ card.unit }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="charts-section">
      <div class="chart-box">
        <h3 class="chart-title">
          <i class="el-icon-trend-charts"></i>
          数据趋势
        </h3>
        <div ref="lineChart" class="chart-wrapper"></div>
      </div>

      <div class="chart-box">
        <h3 class="chart-title">
          <i class="el-icon-pie-chart"></i>
          历史分析统计
        </h3>
        <div ref="pieChart" class="chart-wrapper"></div>
      </div>
    </section>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDashboardSummary, getTrendData, getPlatformDistribution } from '@/api/statistics'

function getRecentDateLabels(days = 7) {
  const labels = []
  const today = new Date()
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(today)
    date.setDate(today.getDate() - i)
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    labels.push(`${month}-${day}`)
  }
  return labels
}

export default {
  name: 'HomePage',
  data() {
    return {
      runningTasks: 0,
      customerFiles: 0,
      totalAnalysisTasks: 0,
      totalVideos: 0,
      remainingPoints: 0,
      consumedPoints: 0,
      totalRechargePoints: 0,
      currentDeductedPoints: 0,
      trendData: [],
      pieChartData: [],
      lineChartInstance: null,
      pieChartInstance: null
    }
  },
  computed: {
    statCards() {
      return [
        { label: '当前运行任务', value: this.runningTasks, unit: '', icon: 'el-icon-video-play', gradient: 'linear-gradient(135deg, #0f9b8e 0%, #1cc7b7 100%)' },
        { label: '客户档案', value: this.customerFiles, unit: '人', icon: 'el-icon-user', gradient: 'linear-gradient(135deg, #e76f51 0%, #f4a261 100%)' },
        { label: '总分析任务', value: this.totalAnalysisTasks, unit: '', icon: 'el-icon-data-analysis', gradient: 'linear-gradient(135deg, #246bfd 0%, #4fb3ff 100%)' },
        { label: '总视频数', value: this.totalVideos, unit: '', icon: 'el-icon-video-camera', gradient: 'linear-gradient(135deg, #3a7d44 0%, #76c893 100%)' },
        { label: '剩余点数', value: this.remainingPoints, unit: '点', icon: 'el-icon-wallet', gradient: 'linear-gradient(135deg, #d62839 0%, #f77f00 100%)' },
        { label: '消耗点数', value: this.consumedPoints, unit: '点', icon: 'el-icon-sold-out', gradient: 'linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)' },
        { label: '总充值点数', value: this.totalRechargePoints, unit: '点', icon: 'el-icon-bank-card', gradient: 'linear-gradient(135deg, #8d6e63 0%, #d7b49e 100%)' },
        { label: '当前扣除', value: this.currentDeductedPoints, unit: '点', icon: 'el-icon-minus', gradient: 'linear-gradient(135deg, #9d4edd 0%, #c77dff 100%)' }
      ]
    }
  },
  async mounted() {
    await Promise.allSettled([
      this.fetchDashboardSummary(),
      this.fetchTrendData(),
      this.fetchPlatformDistribution()
    ])

    this.$nextTick(() => {
      this.initLineChart()
      this.initPieChart()
    })

    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.lineChartInstance) {
      this.lineChartInstance.dispose()
    }
    if (this.pieChartInstance) {
      this.pieChartInstance.dispose()
    }
  },
  methods: {
    async fetchDashboardSummary() {
      try {
        const response = await getDashboardSummary()
        const data = response.data
        this.runningTasks = data.running_tasks || 0
        this.customerFiles = data.customer_files || 0
        this.totalAnalysisTasks = data.total_analysis_tasks || 0
        this.totalVideos = data.total_videos || 0
        this.remainingPoints = data.remaining_points || 0
        this.consumedPoints = data.consumed_points || 0
        this.totalRechargePoints = data.total_recharge_points || 0
        this.currentDeductedPoints = data.current_deducted_points || 0
      } catch (error) {
        console.error('获取仪表盘摘要数据失败:', error)
        this.$message.error('获取统计数据失败')
      }
    },
    async fetchTrendData() {
      try {
        const response = await getTrendData(30)
        this.trendData = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('获取趋势数据失败:', error)
        this.trendData = []
      }
    },
    async fetchPlatformDistribution() {
      try {
        const response = await getPlatformDistribution()
        this.pieChartData = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('获取平台分布数据失败:', error)
        this.pieChartData = []
      }
    },
    initLineChart() {
      const chartDom = this.$refs.lineChart
      if (!chartDom) {
        return
      }

      if (this.lineChartInstance) {
        this.lineChartInstance.dispose()
      }

      this.lineChartInstance = echarts.init(chartDom)
      const hasTrendData = this.trendData.length > 0
      const hasTrendValues = this.trendData.some(item => Number(item.value) > 0)
      const xAxisData = hasTrendData ? this.trendData.map(item => item.date) : getRecentDateLabels(7)
      const seriesData = hasTrendData ? this.trendData.map(item => item.value) : new Array(xAxisData.length).fill(0)
      this.lineChartInstance.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(22, 27, 34, 0.92)',
          borderColor: '#f4a261',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: { lineStyle: { color: '#8a8f98' } },
          axisLabel: { color: '#8a8f98' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#8a8f98' } },
          axisLabel: { color: '#8a8f98' },
          minInterval: 1,
          splitLine: { lineStyle: { color: 'rgba(15, 23, 42, 0.08)' } }
        },
        series: [{
          name: '评论数',
          data: seriesData,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: {
            width: 4,
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#246bfd' },
              { offset: 1, color: '#0f9b8e' }
            ])
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(36, 107, 253, 0.28)' },
              { offset: 1, color: 'rgba(15, 155, 142, 0.04)' }
            ])
          },
          itemStyle: {
            color: '#246bfd',
            borderWidth: 2,
            borderColor: '#fff'
          },
          silent: !hasTrendValues
        }],
        graphic: !hasTrendValues ? {
          type: 'text',
          left: 'center',
          top: '20%',
          style: {
            text: '暂无评论数据',
            fill: '#909399',
            fontSize: 16,
            fontWeight: 500
          }
        } : null
      })
    },
    initPieChart() {
      const chartDom = this.$refs.pieChart
      if (!chartDom) {
        return
      }

      if (this.pieChartInstance) {
        this.pieChartInstance.dispose()
      }

      this.pieChartInstance = echarts.init(chartDom)
      const hasPieData = this.pieChartData.length > 0
      const hasPieValues = this.pieChartData.some(item => Number(item.value) > 0)
      this.pieChartInstance.setOption({
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(22, 27, 34, 0.92)',
          borderColor: '#246bfd',
          textStyle: { color: '#fff' },
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: '5%',
          top: 'center',
          textStyle: { color: '#4b5563', fontSize: 12 },
          itemWidth: 10,
          itemHeight: 10,
          selectedMode: false
        },
        color: ['#246bfd', '#0f9b8e', '#f4a261', '#e76f51', '#9d4edd'],
        series: [
          {
            name: '评论平台',
            type: 'pie',
            radius: ['42%', '72%'],
            center: ['38%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 12,
              borderColor: '#fff',
              borderWidth: 4
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold'
              }
            },
            data: hasPieData ? this.pieChartData : [
              { name: '暂无数据', value: 1, itemStyle: { color: '#e5e7eb' } }
            ],
            silent: !hasPieValues
          }
        ],
        graphic: !hasPieValues ? {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: '暂无评论数据',
            fill: '#909399',
            fontSize: 16,
            fontWeight: 500
          }
        } : null
      })
    },
    handleResize() {
      if (this.lineChartInstance) {
        this.lineChartInstance.resize()
      }
      if (this.pieChartInstance) {
        this.pieChartInstance.resize()
      }
    }
  },
  watch: {
    trendData() {
      this.$nextTick(() => {
        this.initLineChart()
      })
    },
    pieChartData() {
      this.$nextTick(() => {
        this.initPieChart()
      })
    }
  }
}
</script>

<style scoped>
.home-page {
  padding: 24px;
  background:
    radial-gradient(circle at top left, rgba(36, 107, 253, 0.08), transparent 28%),
    radial-gradient(circle at bottom right, rgba(15, 155, 142, 0.1), transparent 24%),
    #f7f9fc;
  min-height: 100%;
}

.stats-section,
.charts-section {
  margin-top: 24px;
}

.stats-section {
  margin-top: 0;
}

.section-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 16px;
}

.section-head h2 {
  margin: 0 0 6px;
  font-size: 22px;
  color: #14213d;
}

.section-head span {
  color: #6b7280;
  font-size: 13px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
}

.card-content {
  min-width: 0;
}

.card-label {
  color: #6b7280;
  font-size: 13px;
}

.card-value {
  margin-top: 6px;
  color: #14213d;
  font-size: 24px;
  font-weight: 700;
}

.charts-section {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.chart-box {
  padding: 20px;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
}

.chart-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  color: #14213d;
}

.chart-wrapper {
  width: 100%;
  height: 360px;
}

@media (max-width: 1200px) {
  .charts-section,
  .stats-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .charts-section,
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .home-page {
    padding: 16px;
  }

  .section-head {
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }
}
</style>
